import { useEffect, useState, lazy, Suspense } from "react";
import { NIEUW_PROJECT } from "../lib/slashopdrachten";
import { useTheme } from "./ThemeProvider";
import { Button } from "./ui/button";
import Noodstop from "./Noodstop";
import { ScrollArea } from "./ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import {
  Plus,
  FileCode,
  MessageSquare,
  MoreHorizontal,
  Trash2,
  Pencil,
  Settings,
  Sun,
  Moon,
  PanelLeftClose,
  PanelLeft,
  Search,
  FileText,
  FileJson,
  FolderOpen,
  Sparkles,
  ChevronDown,
  Folder,
  FolderPlus,
  LogIn,
  LogOut,
  User,
  Users,
  BookOpen,
  Brain,
  Inbox,
  Bot,
} from "lucide-react";
import { cn } from "../lib/utils";

import { useBranding } from "../contexts/BrandingContext";
import { BuildStamp } from "./BuildStamp";
import TaalSchakelaar from "./TaalSchakelaar";
import Beeldmerk from "./Beeldmerk";
import TeamKiezer from "./TeamKiezer";
import { verplaatsDoelen } from "../lib/actiefTeam";

/** Het eigen logo uit de huisstijlinstellingen, of "" voor het NovaMund-beeldmerk. PUUR. J8, 18 okt. */
export const eigenLogoVan = (branding) => (typeof branding?.logo_data_uri === "string" ? branding.logo_data_uri : "");

/** F3b (Frank, 20 oktober 2026): of er een eigen huisstijl staat, met naam of logo.
 *  Dan zegt de zijbalk eronder dat het een product van NovaMund is. PUUR. */
export const eigenHuisstijl = (branding) =>
  Boolean(eigenLogoVan(branding) || (typeof branding?.app_name === "string" && branding.app_name.trim()));
import { useTaal } from "../contexts/TaalContext";
import { opVolgorde } from "../lib/menuvolgorde";
import { herkomstBijDubbel } from "../lib/artefactherkomst";
// NS-A2: lui geladen, want het bekijkvenster trekt react-markdown mee; alleen nodig als er een document staat.
const ArtefactDocument = lazy(() => import("./ArtefactDocument"));
// Vanaf hoeveel projecten het filterveld verschijnt. Een getal met een naam,
// zodat de drempel te lezen en te toetsen is in plaats van verstopt in een
// vergelijking. Gemeten aanleiding: Frank had er 18 na de Brain-import en de
// onderste helft van de lijst was onbereikbaar.
export const FILTER_VANAF = 8;

// PUNT 4c, 11 oktober 2026: alle onderdelen onder EEN inklapbare kop "Menu".
// Op de telefoon standaard dicht, zodat de gesprekken direct zichtbaar zijn; op
// de Mac standaard open. Wat je zelf kiest, wordt onthouden en gaat voor.
export const MENU_OPEN = "novamund.zijmenu.menu-open";

/** De stand van het menu bij het openen. PUUR. */
export function menuBeginstand(opgeslagen, smal) {
  if (opgeslagen === "1") return true;
  if (opgeslagen === "0") return false;
  return !smal;
}

function leesMenuStand() {
  try {
    return window.localStorage.getItem(MENU_OPEN);
  } catch {
    return null;
  }
}

export default function Sidebar({
  conversations,
  currentConversationId,
  isOpen,
  isNarrow = false,
  onToggle,
  onNewChat,
  onSelectConversation,
  onDeleteConversation,
  onRenameConversation,
  onExportConversation,
  onOpenSettings,
  onOpenSearch,
  projects,
  artifacts,
  onCreateProject,
  onSelectProject,
  selectedProjectId,
  onMoveToProject,
  teams = [],
  actiefTeam,
  onMoveToTeam,
  user,
  onOpenLogin,
  onSignOut,
  onOpenUpgrade,
  onNavigateToProject,
  onNavigateToArtifact,
  onNavigateToTeams,
  onNavigateToKnowledge,
  onNavigateToCode,
  onNavigateToAgents,
  onNavigateToMemory,
  // Hoeveel kandidaat-feiten er in het bakje wachten. BLOK 7 fase A.
  // Standaard nul, zodat een aanroeper die er niets van weet ook geen telling
  // laat zien: de afwezigheid van een getal is hier het goede antwoord en niet
  // een gat.
  suggestedCount = 0,
  // HERMES F: wat er in de postbus op een besluit of een blik wacht. Zelfde
  // regel als bij Memory: standaard nul, en nul toont geen getal.
  onNavigateToPostbus,
  postbusCount = 0,
}) {
  const { theme, setTheme } = useTheme();
  const { t, taal } = useTaal();
  // GL212-2: bij een naam die vaker in de lijst staat, uit welk gesprek en wanneer.
  const herkomst = herkomstBijDubbel((artifacts || []).slice(0, 5), conversations, taal);
  const { branding } = useBranding();
  const [renameDialog, setRenameDialog] = useState({ open: false, id: null, title: "" });
  const [projectDialog, setProjectDialog] = useState({ open: false, name: "" });
  // SL-a: /project <naam> in de chat opent dit venster met de naam ingevuld.
  useEffect(() => {
    const open = (e) => setProjectDialog({ open: true, name: e.detail?.naam || "" });
    window.addEventListener(NIEUW_PROJECT, open);
    return () => window.removeEventListener(NIEUW_PROJECT, open);
  }, []);
  // Deleting used to happen the moment the menu item was clicked, with no way
  // back and nothing said about what went with it. A conversation is the one
  // thing here that cannot be remade.
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, title: "" });
  // DE PROJECTLIJST BEGINT INGEKLAPT. Franks opdracht van 23 augustus 2026, en
  // zij DRAAIT EEN EERDER BESLUIT TERUG dat hier stond; dat hoort benoemd te
  // worden in plaats van stil overschreven.
  //
  // WAT ER STOND EN WAAROM: "Projects start open. Collapsed by default meant
  // that on a phone, where the sidebar is a closed drawer to begin with, a
  // project was two discoveries away: open the drawer, then notice that a nav
  // row is also a toggle." Dat bezwaar is niet verdwenen. Het weegt alleen niet
  // meer op tegen wat het kost sinds er echt veel projecten zijn: de lijst duwt
  // bij elke start de gesprekken van het scherm.
  //
  // EEN `useState` EN GEEN `localStorage`, en dat is precies de eis: elke KOUDE
  // start begint ingeklapt, ongeacht wat de vorige sessie deed, en binnen een
  // sessie blijft open wat de gebruiker openklapt. Dit is de ENIGE plek waar
  // die stand ontstaat, dus desktop en PWA doen hetzelfde; er is geen tweede
  // variant om uit de pas te laten lopen.
  const [expandedSections, setExpandedSections] = useState({ chats: true, projects: false, artifacts: false });
  // Wat er in het filterveld staat. Leeg betekent "alles", en dat is iets
  // anders dan "geen treffers": zie de melding onder de lijst.
  const [projectFilter, setProjectFilter] = useState("");
  const [menuOpen, setMenuOpen] = useState(() => menuBeginstand(leesMenuStand(), isNarrow));
  // Draait een venster van smal naar breed en is er nog niets gekozen, dan volgt
  // het menu de breedte. Een eigen keuze blijft staan.
  useEffect(() => {
    if (leesMenuStand() === null) setMenuOpen(menuBeginstand(null, isNarrow));
  }, [isNarrow]);
  const wisselMenu = () => {
    setMenuOpen((open) => {
      const nieuw = !open;
      try {
        window.localStorage.setItem(MENU_OPEN, nieuw ? "1" : "0");
      } catch {
        // Niet kunnen onthouden is geen reden om niet te kunnen klikken.
      }
      return nieuw;
    });
  };

  const handleRename = () => {
    if (renameDialog.title.trim()) {
      onRenameConversation(renameDialog.id, renameDialog.title.trim());
    }
    setRenameDialog({ open: false, id: null, title: "" });
  };

  const handleDelete = () => {
    if (deleteDialog.id) onDeleteConversation(deleteDialog.id);
    setDeleteDialog({ open: false, id: null, title: "" });
  };

  const handleCreateProject = () => {
    if (projectDialog.name.trim()) {
      onCreateProject(projectDialog.name.trim());
    }
    setProjectDialog({ open: false, name: "" });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const filteredConversations = selectedProjectId 
    ? conversations.filter(c => c.project_id === selectedProjectId)
    : conversations.filter(c => !c.project_id);

  // Hoofdletterongevoelig, want wie "agri" typt bedoelt "Plexaris Agri". Een
  // lege waarde geeft ALLES terug en niet niets; dat onderscheid draagt de
  // melding onder de lijst.
  // BESLUIT FRANK 23 okt: de projecten op alfabetische volgorde, zoals het menu.
  const zichtbareProjecten = opVolgorde((projects || []).filter(
    p => !projectFilter || p.name.toLowerCase().includes(projectFilter.toLowerCase())
  ), taal, (p) => p.name || "");

  const planBadgeClass = cn(
    "text-[10px] font-medium px-1.5 py-0.5 rounded uppercase",
    user?.plan === "pro" ? "bg-[hsl(var(--accent))] text-white" :
    user?.plan === "teams" ? "bg-nebula text-white" :
    "bg-muted text-muted-foreground"
  );

  return (
    <>
      {/* On a phone the sidebar lies over the chat instead of beside it. In
          the normal flow it took 280 of 390 points, so the app opened on a
          menu with a sliver of conversation next to it. The backdrop is what
          makes it obvious the sidebar can be dismissed. */}
      {isNarrow && isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50"
          onClick={onToggle}
          aria-hidden="true"
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={cn(
          "flex flex-col border-r bg-[hsl(var(--sidebar))] transition-all duration-300 ease-in-out",
          // overflow-hidden because a zero-width sidebar still painted its own
          // expand button outside itself, so a phone showed two panel icons in
          // the top-left corner: this one and the chat area's. Reported from an
          // iPhone on 2026-07-26.
          "overflow-hidden pt-[var(--veilig-boven)]",
          isNarrow && "fixed inset-y-0 left-0 z-50",
          isOpen ? "w-[280px] max-w-[85vw]" : "w-0 md:w-[60px]"
        )}
        data-testid="sidebar"
      >
        {/* Header */}
        <div className={cn("flex items-center h-12 px-3 gap-2", !isOpen && "justify-center")}>
          {isOpen ? (
            <>
              <div className="flex items-center gap-2 flex-1">
                {/* J8 (18 okt): het eigen beeldmerk als SVG zonder achtergrondvlak; logo.png
                  * heeft een zwarte achtergrond en stond in de lichte stand in een zwart vierkant.
                  * F3b (20 okt), VASTE REGEL VAN FRANK: het NovaMund-beeldmerk staat er ALTIJD
                  * en is niet te vervangen; een klantlogo komt ernaast, nooit in plaats ervan. */}
                <Beeldmerk className="h-7 w-7 shrink-0" testId="zijbalk-beeldmerk" />
                {eigenLogoVan(branding) && (
                  <img src={eigenLogoVan(branding)} alt={branding?.app_name || "Novamund"} className="w-7 h-7 shrink-0 rounded-lg object-contain" data-testid="zijbalk-logo-eigen" />
                )}
                <span className="flex min-w-0 flex-col leading-tight">
                  <span className="font-medium text-sm truncate">{branding?.app_name || "Novamund"}</span>
                  {eigenHuisstijl(branding) && (
                    <span className="text-[10px] text-muted-foreground truncate" data-testid="zijbalk-product-van">
                      {t("een product van NovaMund")}
                    </span>
                  )}
                </span>
              </div>
              <Button variant="ghost" size="icon" onClick={onToggle} className="tap-target h-7 w-7" data-testid="collapse-sidebar-btn">
                <PanelLeftClose className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button variant="ghost" size="icon" onClick={onToggle} className="tap-target h-7 w-7" data-testid="expand-sidebar-btn">
              <PanelLeft className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* K2 (18 okt): de werkruimte, direct onder de kop. Alleen uitgeklapt; de
            telefoon gebruikt dezelfde zijbalk en krijgt dus dezelfde kiezer. */}
        {isOpen && <TeamKiezer />}

        {/* New Chat & Navigation */}
        <div className={cn("px-2 space-y-1", !isOpen && "hidden md:block md:px-2")}>
          <Button onClick={onNewChat} variant="ghost" className={cn("w-full justify-start gap-2 h-9", !isOpen && "md:justify-center md:px-2")} data-testid="new-chat-btn">
            <Plus className="h-4 w-4" />
            {isOpen && <span>{t("menu.nieuweChat")}</span>}
          </Button>

          {isOpen && (
            <Button variant="ghost" onClick={wisselMenu} aria-expanded={menuOpen}
              data-testid="menu-toggle"
              className="w-full justify-start gap-2 h-8 text-xs font-medium uppercase text-muted-foreground">
              <span>{t("menu.menu")}</span>
              <ChevronDown className={cn("h-3 w-3 ml-auto transition-transform", menuOpen && "rotate-180")} />
            </Button>
          )}
          {/* Ingeklapt als zijbalk (alleen iconen) blijven de iconen staan: daar is geen kop. */}
          {(menuOpen || !isOpen) && (
          <div data-testid="menu-onderdelen" className="space-y-1">
          <Button variant="ghost" onClick={onOpenSearch} className={cn("w-full justify-start gap-2 h-9 text-muted-foreground", !isOpen && "md:justify-center md:px-2")}>
            <Search className="h-4 w-4" />
            {isOpen && <span>{t("menu.chats")}</span>}
          </Button>

          {/* BESLUIT FRANK 23 OKTOBER 2026: Chats staat bovenaan, en daarna alles op
              alfabetische volgorde IN DE GEKOZEN TAAL (`opVolgorde`). De volgorde
              staat dus niet in de code maar volgt uit de labels; tellers en stippen
              horen bij hun eigen knop en verhuizen mee. */}
          {opVolgorde([
            { label: t("menu.projecten"), knop: (
            <Button key="projecten" variant="ghost" data-testid="projects-toggle" aria-expanded={expandedSections.projects} onClick={() => isOpen && toggleSection("projects")} className={cn("w-full justify-start gap-2 h-9 text-muted-foreground", !isOpen && "md:justify-center md:px-2")}>
              <FolderOpen className="h-4 w-4" />
              {isOpen && <span>{t("menu.projecten")}</span>}
              {isOpen && <ChevronDown className={cn("h-3 w-3 ml-auto transition-transform", expandedSections.projects && "rotate-180")} />}
            </Button>
            ) },
            { label: t("menu.artefacten"), knop: (
            <Button key="artefacten" variant="ghost" onClick={() => isOpen && toggleSection("artifacts")} className={cn("w-full justify-start gap-2 h-9 text-muted-foreground", !isOpen && "md:justify-center md:px-2")}>
              <Sparkles className="h-4 w-4" />
              {isOpen && <span>{t("menu.artefacten")}</span>}
              {isOpen && <ChevronDown className={cn("h-3 w-3 ml-auto transition-transform", expandedSections.artifacts && "rotate-180")} />}
            </Button>
            ) },
          /* G4 (Frank, 18 okt 2026): Abonnement, Netwerk en Winkel staan in
              Instellingen (tabbladen Abonnement en Netwerk), niet meer hier. */
            { label: t("menu.knowledge"), knop: (
            <Button key="knowledge" variant="ghost" onClick={() => onNavigateToKnowledge?.()} className={cn("w-full justify-start gap-2 h-9 text-muted-foreground", !isOpen && "md:justify-center md:px-2")}>
              <BookOpen className="h-4 w-4" />
              {isOpen && <span>{t("menu.knowledge")}</span>}
            </Button>
            ) },
            /* BESLUIT FRANK 23 okt (deel 3): Skills en Koppelingen staan niet meer in
                dit menu; ze openen alleen via het plusmenu onder het chatvenster. */
          /* Next to what fills it. An agent is made of skills and knowledge,
              so it belongs beside those and not down among the account
              things. */
            { label: t("menu.agenten"), knop: (
            <Button key="agenten" variant="ghost" onClick={() => onNavigateToAgents?.()} className={cn("w-full justify-start gap-2 h-9 text-muted-foreground", !isOpen && "md:justify-center md:px-2")} data-testid="agents-nav-btn">
              <Bot className="h-4 w-4" />
              {isOpen && <span>{t("menu.agenten")}</span>}
            </Button>
            ) },
            { label: t("menu.code"), knop: (
            <Button key="code" variant="ghost" onClick={() => onNavigateToCode?.()} className={cn("w-full justify-start gap-2 h-9 text-muted-foreground", !isOpen && "md:justify-center md:px-2")} data-testid="code-nav-btn">
              <FileCode className="h-4 w-4" />
              {isOpen && <span>{t("menu.code")}</span>}
            </Button>
            ) },
          /* DE TELLING IS EIS 5 VAN HET BOUWPLAN, en de vorm is de eis en niet
              de smaak: "zichtbaar als een regel met een telling in de zijbalk.
              Geen pop-up, nooit."
              Dus GEEN rood bolletje en geen felle kleur. Een rood bolletje is
              een eis om aandacht, en dit bakje vraagt toestemming; het verschil
              tussen die twee is precies waar deze functie aan kan sterven.
              De telling staat OOK in het toegankelijke label, want bij een
              ingeklapte zijbalk is het cijfer het enige dat er nog is, en voor
              wie met een schermlezer werkt is het label het enige dat er ooit
              is. */
            { label: t("menu.geheugen"), knop: (
            <Button key="geheugen"
              variant="ghost"
              onClick={() => onNavigateToMemory?.()}
              className={cn("w-full justify-start gap-2 h-9 text-muted-foreground", !isOpen && "md:justify-center md:px-2")}
              data-testid="memory-nav-btn"
              aria-label={suggestedCount > 0 ? t("menu.geheugenVoorgesteld", { n: suggestedCount }) : t("menu.geheugen")}
            >
              {/* DE STIP, op Franks verzoek van 24 augustus 2026: de telling
                  alleen zag hij niet ("die zie ik niet als ik niet naar Memory
                  ga"). Een stip leest sneller dan een cijfer tussen tekst.

                  **HIJ IS NIET ROOD, en dat is dezelfde afweging als hierboven:**
                  rood is een eis om aandacht, en dit bakje vraagt toestemming.
                  Hij gebruikt de gewone voorgrondkleur, dus hij valt op zonder te
                  schreeuwen. Voor wie hem niet ziet blijft het cijfer staan, en
                  voor wie met een schermlezer werkt het label; de stip draagt
                  daarom `aria-hidden` en is puur een tweede weg naar dezelfde
                  mededeling. */}
              <span className="relative flex-shrink-0">
                <Brain className="h-4 w-4" />
                {suggestedCount > 0 && (
                  <span
                    data-testid="memory-nav-dot"
                    aria-hidden="true"
                    className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-foreground"
                  />
                )}
              </span>
              {isOpen && <span>{t("menu.geheugen")}</span>}
              {suggestedCount > 0 && (
                <span
                  data-testid="memory-nav-count"
                  className={cn("rounded-full border px-1.5 text-xs leading-tight", isOpen && "ml-auto")}
                >
                  {suggestedCount}
                </span>
              )}
            </Button>
            ) },
          /* DE POSTBUS, Hermes F. Dezelfde vorm als Memory hierboven: een stip,
              een cijfer en het cijfer ook in het label, en niet rood, want de
              postbus vraagt een besluit en geen alarm. */
            { label: t("menu.postbus"), knop: (
            <Button key="postbus"
              variant="ghost"
              onClick={() => onNavigateToPostbus?.()}
              className={cn("w-full justify-start gap-2 h-9 text-muted-foreground", !isOpen && "md:justify-center md:px-2")}
              data-testid="postbus-nav-btn"
              aria-label={postbusCount > 0 ? t("menu.postbusWachtend", { n: postbusCount }) : t("menu.postbus")}
            >
              <span className="relative flex-shrink-0">
                <Inbox className="h-4 w-4" />
                {postbusCount > 0 && (
                  <span
                    data-testid="postbus-nav-dot"
                    aria-hidden="true"
                    className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-foreground"
                  />
                )}
              </span>
              {isOpen && <span>{t("menu.postbus")}</span>}
              {postbusCount > 0 && (
                <span
                  data-testid="postbus-nav-count"
                  className={cn("rounded-full border px-1.5 text-xs leading-tight", isOpen && "ml-auto")}
                >
                  {postbusCount}
                </span>
              )}
            </Button>
            ) },
            // BESLUIT FRANK 23 okt: Teams staat weer in het menu, niet meer onder Projecten.
            { label: t("menu.teams"), knop: (
            <Button key="teams" variant="ghost" onClick={() => onNavigateToTeams?.()} className={cn("w-full justify-start gap-2 h-9 text-muted-foreground", !isOpen && "md:justify-center md:px-2")} data-testid="teams-nav-btn">
              <Users className="h-4 w-4" />
              {isOpen && <span>{t("menu.teams")}</span>}
            </Button>
            ) },
          ], taal).map((i) => i.knop)}
          </div>
          )}
        </div>

        {/* Projects Section */}
        {isOpen && expandedSections.projects && (
          <div className="px-2 py-2 border-t mt-2">
            <div className="flex items-center justify-between px-2 mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase">{t("menu.projecten")}</span>
              <Button variant="ghost" size="icon" className="tap-target h-8 w-8 md:h-6 md:w-6" data-testid="new-project-btn" aria-label={t("menu.nieuwProject")} onClick={() => setProjectDialog({ open: true, name: "" })}>
                <FolderPlus className="h-4 w-4 md:h-3 md:w-3" />
              </Button>
            </div>
            {/* BUITEN het scrollgebied, en dat is met opzet. Dit is de weg
                terug naar alles; die mag niet uit beeld scrollen en ook niet
                verdwijnen zodra er een filter staat. */}
            <div className="space-y-1">
              {/* BESLUIT FRANK 23 OKTOBER 2026: Alle chats staat hier, bovenaan het blok
                  Projecten, als de weg terug uit een gekozen project naar alle chats.
                  Teams staat sindsdien in het menu. Buiten het scrollgebied. */}
              <Button
                variant={!selectedProjectId ? "secondary" : "ghost"}
                className="w-full justify-start gap-2 h-8 text-sm"
                data-testid="alle-chats"
                onClick={() => onSelectProject?.(null)}
              >
                <MessageSquare className="h-3.5 w-3.5" />
                {t("Alle chats")}
              </Button>
            </div>

            {/* Het zoekveld pas vanaf FILTER_VANAF projecten. Bij drie is het
                ruis, bij achttien is de onderste helft anders onbereikbaar. De
                drempel staat als constante, want een drempel die nergens staat
                is een regel die niemand ziet. */}
            {projects?.length >= FILTER_VANAF && (
              <Input
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                placeholder={t("menu.projectenFilteren")}
                aria-label={t("menu.projectenFilteren")}
                data-testid="project-filter"
                className="h-8 text-sm mt-1"
              />
            )}

            {/* EIGEN scrollgebied, niet op de zijbalk zelf: die draagt
                `overflow-hidden` (regel 157) en de kop en New chat hangen daar
                ook in. Zonder deze twee klassen groeit de lijst door tot buiten
                de zijbalk en wordt zij daar stilzwijgend afgeknipt. Gemeten met
                18 projecten: de onderste helft was onbereikbaar. */}
            <div
              className="space-y-1 mt-1 max-h-[40vh] overflow-y-auto"
              data-testid="project-list"
            >
              {zichtbareProjecten.length === 0 && projectFilter && (
                // Een leeg vak leest als "je hebt geen projecten". Dat is iets
                // anders dan "geen enkele naam bevat wat je typte".
                <p className="px-2 py-1.5 text-xs text-muted-foreground">
                  {t("Geen project met \"{filter}\"", { filter: projectFilter })}
                </p>
              )}
              {zichtbareProjecten.map((project) => (
                <div key={project.id} className="group flex items-center">
                  <Button
                    variant={selectedProjectId === project.id ? "secondary" : "ghost"}
                    className="flex-1 justify-start gap-2 h-8 text-sm"
                    onClick={() => onSelectProject?.(project.id)}
                  >
                    <Folder className="h-3.5 w-3.5" style={{ color: project.color }} />
                    <span className="truncate">{project.name}</span>
                  </Button>
                  <button
                    onClick={() => onNavigateToProject?.(project.id)}
                    className="tap-target h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-foreground"
                    title={t("menu.projectOpenen")}
                  >
                    <FolderOpen className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Artifacts Section */}
        {isOpen && expandedSections.artifacts && (
          <div className="px-2 py-2 border-t">
            <div className="px-2 mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase">{t("menu.artefacten")}</span>
            </div>
            <div className="space-y-1">
              {artifacts?.length > 0 ? artifacts.slice(0, 5).map((artifact) => artifact.bijlage ? (
                // NS-A2: een bewaard document opent in het bekijkvenster, niet op ArtifactPage.
                <Suspense key={artifact.id} fallback={null}>
                  <ArtefactDocument artefact={artifact} herkomst={herkomst[artifact.id] || ""}
                    className="w-full px-2 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors" />
                </Suspense>
              ) : (
                <button
                  key={artifact.id}
                  onClick={() => onNavigateToArtifact?.(artifact.id, artifact.conversation_id)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors text-left truncate"
                >
                  <FileText className="h-3 w-3 flex-shrink-0" />
                  <span className="min-w-0 flex flex-col">
                    <span className="truncate">{artifact.name}</span>
                    {herkomst[artifact.id] && <span className="truncate text-[11px]" data-testid="artefact-herkomst">{herkomst[artifact.id]}</span>}
                  </span>
                </button>
              )) : (
                <p className="px-2 py-1.5 text-xs text-muted-foreground">{t("menu.nogGeenArtefacten")}</p>
              )}
            </div>
          </div>
        )}

        {/* Recents */}
        {/* `[&>div>div]:!block` is the fix for the three dots nobody could
            find. Radix renders the scroll viewport's child as `display:
            table` with `min-width:100%`, and a table shrink-wraps to its
            content instead of obeying its container: measured at 398 points
            inside a 279 point scroller. Every conversation row inherited that
            width, so the menu button was laid out at x=358 in a 280 point
            sidebar and clipped away by the sidebar's own overflow. Forcing
            that one wrapper to block makes the width real again, which is
            what lets `truncate` on the title do its job. */}
        <ScrollArea className={cn("flex-1 [&>div>div]:!block", !isOpen && "hidden md:block")}>
          <div className="px-2 py-2">
            {isOpen && (
              <p className="px-2 py-2 text-xs font-medium text-muted-foreground uppercase">
                {selectedProjectId ? t("menu.projectchats") : t("menu.recent")}
              </p>
            )}
            {filteredConversations.map((conv) => (
              <div
                key={conv.id}
                className={cn(
                  "group relative flex items-center rounded-lg mb-0.5 transition-colors",
                  currentConversationId === conv.id ? "bg-[hsl(var(--muted))]" : "hover:bg-[hsl(var(--muted))]"
                )}
              >
                {/* min-w-0, and that word is the whole bug. A flex item's
                    minimum width is its content unless you say otherwise, so
                    this button refused to shrink, `truncate` on the title
                    below it could never fire, and the row grew to 382 points
                    inside a 280 point sidebar. The three dots were rendered at
                    x=358 and clipped away by the sidebar's own overflow. Not
                    hidden by a hover rule, as it looked: simply off the edge,
                    on desktop and on a phone alike. */}
                <button
                  onClick={() => onSelectConversation(conv.id)}
                  className={cn("min-w-0 flex-1 flex items-center gap-2 px-2 py-2 text-left", !isOpen && "justify-center")}
                  data-testid={`conversation-${conv.id}`}
                >
                  <MessageSquare className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                  {isOpen && <span className="truncate text-sm">{conv.title}</span>}
                </button>
                
                {isOpen && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="tap-target h-8 w-8 md:h-7 md:w-7 mr-1 text-muted-foreground hover:text-foreground" data-testid={`conversation-menu-${conv.id}`}>
                        <MoreHorizontal className="h-4 w-4 md:h-3.5 md:w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={() => setRenameDialog({ open: true, id: conv.id, title: conv.title })}>
                        <Pencil className="h-4 w-4 mr-2" />{t("menu.hernoemen")}
                      </DropdownMenuItem>
                      {/* GL199-2 (glasronde 0.9.199): verplaatsen staat elk in een eigen submenu,
                          zodat het menu kort blijft en Exporteren en Verwijderen zonder scrollen
                          in beeld staan, hoeveel projecten en werkruimtes er ook zijn. */}
                      {(projects?.length > 0 || teams?.length > 0) && <DropdownMenuSeparator />}
                      {projects?.length > 0 && (
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger data-testid={`verplaats-project-${conv.id}`}>
                            <Folder className="h-4 w-4" />{t("Verplaats naar project")}
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
                            {projects.map((p) => (
                              <DropdownMenuItem key={p.id} onClick={() => onMoveToProject?.(conv.id, p.id)}
                                                data-testid={`naar-project-${p.id}`}>
                                <Folder className="h-4 w-4 mr-2" style={{ color: p.color }} />{p.name}
                              </DropdownMenuItem>
                            ))}
                            {conv.project_id && (
                              <DropdownMenuItem onClick={() => onMoveToProject?.(conv.id, null)}>
                                <MessageSquare className="h-4 w-4 mr-2" />{t("menu.uitProject")}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      )}
                      {/* K3 (18 okt): naar een andere werkruimte, zoals naar een project. */}
                      {teams?.length > 0 && (
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger data-testid={`verplaats-werkruimte-${conv.id}`}>
                            <Users className="h-4 w-4" />{t("Verplaats naar werkruimte")}
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
                            {verplaatsDoelen(teams, actiefTeam, t).map((o) => (
                              <DropdownMenuItem key={o.id || "persoonlijk"} data-testid={`naar-werkruimte-${o.id || "persoonlijk"}`}
                                                onClick={() => onMoveToTeam?.(conv.id, o.id)}>
                                <Users className="h-4 w-4 mr-2" />{o.naam}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => onExportConversation(conv.id, "markdown")}>
                        <FileText className="h-4 w-4 mr-2" />{t("menu.exportMarkdown")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onExportConversation(conv.id, "json")}>
                        <FileJson className="h-4 w-4 mr-2" />{t("menu.exportJson")}
                      </DropdownMenuItem>
                      {/* CC5: het hele gesprek als Word of PDF. */}
                      <DropdownMenuItem onClick={() => onExportConversation(conv.id, "docx")} data-testid={`export-docx-${conv.id}`}>
                        <FileText className="h-4 w-4 mr-2" />{t("menu.exportWord")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onExportConversation(conv.id, "pdf")} data-testid={`export-pdf-${conv.id}`}>
                        <FileText className="h-4 w-4 mr-2" />{t("menu.exportPdf")}
                      </DropdownMenuItem>
                      {/* GL215 S2: gesprek, bestanden en spoor als een dossier met manifest. */}
                      <DropdownMenuItem onClick={() => onExportConversation(conv.id, "dossier")} data-testid={`export-dossier-${conv.id}`}>
                        <FileJson className="h-4 w-4 mr-2" />{t("Dossier (zip)")}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setDeleteDialog({ open: true, id: conv.id, title: conv.title })}
                        className="text-destructive focus:text-destructive"
                        data-testid={`delete-conversation-${conv.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />{t("menu.chatVerwijderenKnop")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className={cn("border-t p-2 space-y-1", !isOpen && "hidden md:block md:px-2")}>
          {isOpen && user?.plan === "free" && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenUpgrade}
              className="w-full justify-center gap-2 h-8 text-xs border-[hsl(var(--accent))] text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))/10]"
            >
              <Sparkles className="h-3 w-3" />
              <span>{t("menu.upgrade")}</span>
            </Button>
          )}
          <Button variant="ghost" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className={cn("w-full justify-start gap-2 h-9", !isOpen && "md:justify-center md:px-2")} data-testid="theme-toggle-btn">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            {isOpen && <span>{theme === "dark" ? t("menu.licht") : t("menu.donker")}</span>}
          </Button>
          {/* DE NOODSTOP, POST 5. In de zijbalk en niet achter een tabblad:
              een rem die je eerst moet zoeken werkt niet op het moment dat je
              hem nodig hebt. */}
          <Noodstop compact={!isOpen} />
          {/* DE TAALSCHAKELAAR (NL/EN-batch A1), zichtbaar bereikbaar en niet
              alleen achter Instellingen. */}
          {isOpen && <TaalSchakelaar />}
          <Button variant="ghost" onClick={onOpenSettings} className={cn("w-full justify-start gap-2 h-9", !isOpen && "md:justify-center md:px-2")} data-testid="settings-btn">
            <Settings className="h-4 w-4" />
            {isOpen && <span>{t("menu.instellingen")}</span>}
          </Button>
          {isOpen && user && (
            <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground border-t pt-2 mt-1">
              <User className="h-3 w-3" />
              <span className="truncate flex-1">{!user.display_name || user.display_name === "Local User" ? t("menu.lokaleGebruiker") : user.display_name}</span>
              <span className={planBadgeClass}>{user.plan || "free"}</span>
            </div>
          )}
          {/* One of these two, never both.
              `user.id` is the difference between a session and a caption: an
              account that came back from /auth/me has one, and the "Local
              User" the app puts up when it has no token does not. So the way
              out is offered exactly when there is something to leave, and the
              way in exactly when there is not. */}
          {isOpen && user?.id && onSignOut && (
            <Button variant="ghost" size="sm" onClick={onSignOut} className="w-full justify-start gap-2 h-8 text-xs" data-testid="sign-out-btn">
              <LogOut className="h-3 w-3" />
              <span>{t("menu.afmelden")}</span>
            </Button>
          )}
          {isOpen && !user?.id && onOpenLogin && (
            <Button variant="ghost" size="sm" onClick={onOpenLogin} className="w-full justify-start gap-2 h-8 text-xs">
              <LogIn className="h-3 w-3" />
              <span>{t("menu.aanmelden")}</span>
            </Button>
          )}
          {/* Always in view when the sidebar is open, so "which build am I
              looking at" is a glance instead of an investigation. */}
          {isOpen && <BuildStamp className="pt-1" />}
        </div>
      </aside>

      {/* Rename Dialog */}
      <Dialog open={renameDialog.open} onOpenChange={(open) => !open && setRenameDialog({ open: false, id: null, title: "" })}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("menu.chatHernoemen")}</DialogTitle></DialogHeader>
          <Input value={renameDialog.title} onChange={(e) => setRenameDialog(prev => ({ ...prev, title: e.target.value }))} placeholder={t("menu.nieuweTitel")} onKeyDown={(e) => e.key === "Enter" && handleRename()} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialog({ open: false, id: null, title: "" })}>{t("menu.annuleren")}</Button>
            <Button onClick={handleRename}>{t("menu.opslaan")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Dialog */}
      <Dialog open={projectDialog.open} onOpenChange={(open) => !open && setProjectDialog({ open: false, name: "" })}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("menu.projectAanmaken")}</DialogTitle></DialogHeader>
          <Input value={projectDialog.name} onChange={(e) => setProjectDialog(prev => ({ ...prev, name: e.target.value }))} placeholder={t("menu.projectnaam")} onKeyDown={(e) => e.key === "Enter" && handleCreateProject()} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setProjectDialog({ open: false, name: "" })}>{t("menu.annuleren")}</Button>
            <Button onClick={handleCreateProject}>{t("menu.aanmaken")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog. It names the chat and says what goes with it, because
          "Are you sure?" tells a person nothing they did not already know. The
          confirming button carries the word delete rather than "OK", so the
          one that cannot be undone is not the one you press by reflex. */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, id: null, title: "" })}>
        <DialogContent data-testid="delete-conversation-dialog">
          <DialogHeader><DialogTitle>{t("menu.chatVerwijderen")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            <span className="text-foreground font-medium">{deleteDialog.title || t("menu.dezeChat")}</span>{" "}
            {t("menu.verwijderUitleg")}
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              className="tap-target"
              onClick={() => setDeleteDialog({ open: false, id: null, title: "" })}
              data-testid="delete-cancel"
            >
              {t("menu.annuleren")}
            </Button>
            <Button
              variant="destructive"
              className="tap-target"
              onClick={handleDelete}
              data-testid="delete-confirm"
            >
              <Trash2 className="h-4 w-4 mr-2" />{t("menu.chatVerwijderenKnop")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
